/**
 * "Widget configuration" (the data-* reference) — Widget section (ADR-0074).
 *
 * Reconciled attribute-by-attribute against what the bootstrap actually reads
 * off the script tag. The page's own lead promises "the full set", which makes
 * an omission a false statement rather than a gap: the surface audit found
 * eleven attributes missing, most of them opt-ins for features documented
 * elsewhere — so the docs described a capability and then did not say how to
 * switch it on.
 *
 * Three groups now, because the third is a different KIND of attribute: core
 * and advanced configure a widget that is already doing its job, while the
 * opt-ins turn a whole behaviour on or off. Mixing them put "let visitors
 * select part of the page and ask about it" in the same list as "which corner
 * the launcher sits in".
 */
import type { DocPageContent } from '../types';

const EXAMPLE = `<script
  type="module"
  src="https://cdn.syncanix.com/widget.js"
  data-key="pk_test_..."
  data-position="bottom-left"
  data-env="development"
  data-chat-size="large"
  data-locale="fr"
  data-config-load="eager"
></script>`;

export const WIDGET_CONFIG_PAGE: DocPageContent = {
  slug: 'widget/configuration',
  titleKey: 'docs.widget.config.title',
  descriptionKey: 'docs.widget.config.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.widget.config.lead' },
    {
      kind: 'code',
      language: 'html',
      code: EXAMPLE,
      captionKey: 'docs.widget.config.example.caption',
    },

    { kind: 'heading', level: 2, id: 'core', textKey: 'docs.widget.config.core.heading' },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.config.attr.key',
        'docs.widget.config.attr.enabled',
        'docs.widget.config.attr.position',
        'docs.widget.config.attr.locale',
        'docs.widget.config.attr.size',
        'docs.widget.config.attr.env',
        'docs.widget.config.attr.theme',
      ],
    },
    // ADR-0411 — automatic host-page language following. Integrator-facing
    // because it is a DEFAULT with no attribute: nothing in the list above hints
    // that omitting `data-locale` is the active choice rather than the absent one.
    { kind: 'paragraph', textKey: 'docs.widget.config.autoLanguage' },
    // ADR-0413 — the chrome/reply-hint split. Integrator-facing because it is the
    // one place the two visibly disagree: English labels around a Hebrew thread.
    { kind: 'paragraph', textKey: 'docs.widget.config.conversationLanguage' },

    { kind: 'heading', level: 2, id: 'advanced', textKey: 'docs.widget.config.advanced.heading' },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.config.attr.origin',
        'docs.widget.config.attr.themeName',
        'docs.widget.config.attr.promptProfile',
        'docs.widget.config.attr.mountTarget',
        'docs.widget.config.attr.apiBaseUrl',
        'docs.widget.config.attr.apiOrigins',
        'docs.widget.config.attr.themeVars',
        'docs.widget.config.attr.configLoad',
        'docs.widget.config.attr.executor',
        'docs.widget.config.attr.tokenProvider',
        'docs.widget.config.attr.stepUpProvider',
        'docs.widget.config.attr.headersProvider',
        'docs.widget.config.attr.clientWitness',
        'docs.widget.config.attr.actOnBehalf',
      ],
    },
    { kind: 'heading', level: 2, id: 'opt-ins', textKey: 'docs.widget.config.optIn.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.config.optIn.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.config.attr.trigger',
        'docs.widget.config.attr.askAbout',
        'docs.widget.config.attr.haptics',
        'docs.widget.config.attr.frameMode',
        'docs.widget.config.attr.frameOrigins',
        'docs.widget.config.attr.captureClosedRoots',
      ],
    },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.widget.config.optIn.note.title',
      textKey: 'docs.widget.config.optIn.note.body',
      links: {
        labs: {
          target: { docSlug: 'dashboard/labs' },
          labelKey: 'docs.widget.config.optIn.note.link',
        },
      },
    },

    // PR-1.6 — the capture-masking (privacy) controls. A separate KIND of
    // attribute again: these decide what the assistant may SEE of your page, so a
    // value on screen never has to reach it. Called out on its own because a
    // privacy control a customer cannot find is effectively absent.
    { kind: 'heading', level: 2, id: 'masking', textKey: 'docs.widget.config.masking.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.config.masking.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.config.attr.blockSelectors',
        'docs.widget.config.attr.maskSelectors',
        'docs.widget.config.attr.maskPii',
      ],
    },

    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.widget.config.runtime.title',
      textKey: 'docs.widget.config.runtime.body',
      links: {
        api: {
          target: { docSlug: 'widget/runtime-api' },
          labelKey: 'docs.widget.config.runtime.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.widget.config.next.api.title',
          textKey: 'docs.widget.config.next.api.text',
          to: { docSlug: 'widget/runtime-api' },
        },
        {
          titleKey: 'docs.widget.config.next.theming.title',
          textKey: 'docs.widget.config.next.theming.text',
          to: { docSlug: 'widget/theming' },
        },
      ],
    },
  ],
};
